// Automatic currency resolution — no manual override.
//
// Product rule: India → INR (₹), every other country → USD ($).
// USD is treated as the base display currency, so `getLocalCurrency()`
// returns `null` for non-Indian visitors and consumers render USD only.
//
// Detection order:
//   1. Fresh cache (≤ 6 h) — `localStorage.mrd_local_currency_v1`.
//   2. IP geolocation via ipwho.is (free, CORS-friendly, no key).
//   3. Browser locale — `navigator.language`.
//   Any failure → return null (USD-only display).

const CACHE_KEY = "mrd_local_currency_v1";
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 h

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.at || Date.now() - obj.at > CACHE_TTL_MS) return null;
    return obj;
  } catch (_) { return null; }
}

function writeCache(payload) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...payload, at: Date.now() }));
  } catch (_) { /* ignore */ }
}

async function fetchINRRate() {
  try {
    const res = await fetch("https://api.frankfurter.dev/latest?from=USD&to=INR");
    if (!res.ok) return null;
    const json = await res.json();
    const rate = json?.rates?.INR;
    if (!rate || typeof rate !== "number") return null;
    return rate;
  } catch (_) { return null; }
}

function localeIsIndia() {
  try {
    const lang = navigator.language || (navigator.languages && navigator.languages[0]);
    if (!lang) return false;
    try {
      const region = new Intl.Locale(lang).region;
      if (region) return region.toUpperCase() === "IN";
    } catch (_) { /* older browsers */ }
    const parts = lang.split(/[-_]/);
    return parts.length >= 2 && parts[1].toUpperCase() === "IN";
  } catch (_) { return false; }
}

async function ipIsIndia() {
  try {
    const res = await fetch("https://ipwho.is/", { cache: "no-store" });
    if (!res.ok) return false;
    const json = await res.json();
    if (json?.success === false) return false;
    return (json?.country_code || "").toUpperCase() === "IN";
  } catch (_) { return false; }
}

/**
 * Returns `{ currency: 'INR', symbol: '₹', rate: <USD→INR> }` for visitors
 * detected in India, or `null` for everyone else (USD-only display).
 * Consumers must handle `null` by rendering the USD price only.
 */
export async function getLocalCurrency() {
  const cached = readCache();
  if (cached && cached.currency === "INR") return cached;

  // IP first — it's the most reliable signal, especially for mobile users
  // whose browser locale often reads `en-US` regardless of physical country.
  const inIndia = (await ipIsIndia()) || localeIsIndia();
  if (!inIndia) return null;

  const rate = await fetchINRRate();
  if (!rate) return null;

  const payload = { currency: "INR", symbol: "₹", rate };
  writeCache(payload);
  return payload;
}

/** Formats a USD amount as its INR equivalent for display next to the USD price. */
export function formatLocalPrice(usd, local) {
  if (!local || local.currency !== "INR") return null;
  const converted = usd * local.rate;
  return `${local.symbol}${converted.toFixed(2)}`;
}

/**
 * Location-aware label for the primary "Download Resume" CTA.
 *  • India (localCurrency.currency === 'INR') → "Download Resume for ₹99"
 *  • Everyone else (or detection failed)      → "Download Resume for USD $1"
 *
 * When an active `discount` is passed we substitute the discounted
 * price directly into the CTA so the label reflects what the user will
 * actually be charged (instead of showing the pre-discount rate next to
 * a strikethrough copy of the same number).
 *
 * NOTE: ₹99 is a fixed marketing price point chosen by the product owner.
 * The actual Razorpay charge is still driven by `payCfg.amount_usd` server
 * side, so this label is purely for display/localization.
 */
export function getDownloadCtaLabel(local, discount) {
  const isIndia = local && local.currency === "INR";
  const hasDiscount = discount && typeof discount.percent === "number" && discount.percent > 0;
  if (isIndia) {
    if (hasDiscount) {
      const discountedInr = Math.max(1, Math.round(99 * (1 - discount.percent / 100)));
      return `Download Resume for ₹${discountedInr}`;
    }
    return "Download Resume for ₹99";
  }
  if (hasDiscount) {
    const finalUsd = typeof discount.final_usd === "number" ? discount.final_usd : null;
    const price = finalUsd != null ? finalUsd.toFixed(2) : "1.00";
    return `Download Resume for USD $${price}`;
  }
  return "Download Resume for USD $1";
}
